import { NoticeBoardService } from '@administration/notice-board/notice-board.service';
import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { TabNotificationsComponent } from '@common/components/tab-notifications/tab-notifications.component';
import { EnvironmentNames } from '@common/knownTypes/EnvironmentNames';
import { KendoMessageService } from '@common/services/kendoMessage.service';
import { marker } from '@common/translate-marker';
import { isMobile } from '@common/utils/isMobile';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult, DialogService } from '@progress/kendo-angular-dialog';
import { MessageService } from '@progress/kendo-angular-l10n';
import { ToastService } from '@services/toast.service';
import { StateService } from 'app/singleton-services/state.service';
import { environment } from 'environments/environment';
import { some } from 'lodash-es';
import { filter, tap } from 'rxjs/operators';
import { MenuItem } from '../../models/common/MenuItem';
import { UserModel } from '../../models/User.model';
import { ModalService, ModalWidth } from '../../services/modal.service';
import { currentUserSubject, UserService } from '../../services/user.service';
import { ChangeLanguageHeaderComponent } from '../change-language-header/change-language-header.component';

@Component({
    selector: 'app-header',
    templateUrl: 'header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderComponent implements OnInit {
    @Output() toggleSidebar = new EventEmitter();
    user: UserModel;
    dark: boolean;

    buildInfo = environment.name;
    displayBuildInfo = environment.name !== EnvironmentNames.PRODUCTION;

    isMobile = isMobile();

    profileMenuItems = ['account', 'sign-out'];

    userLoggedOutMenuItems = [
        { id: 'EN', name: marker('EN - English') },
        { id: 'BG', name: marker('BG - български') }
    ];

    notificationsCount: number = 0;

    constructor(
        private router: Router,
        private dialogService: ModalService,
        private translateService: TranslateService,
        private userService: UserService,
        private renderer: Renderer2,
        private kendoDialog: DialogService,
        private toastyService: ToastService,
        private noticeBoardService: NoticeBoardService,
        private stateService: StateService,
        private messageService: MessageService
    ) {
        this.user = null;

        // The header only mirrors currentUserSubject. It used to issue its own getCurrentUser request
        // here and push the answer back into the subject; StartupService already loads the user before
        // this component exists, so that request was redundant - and because it raced the logout
        // triggered right after a page load (stale ping / expired session), its late response could
        // re-publish the user after LogoutComponent had already emitted null, leaving the sidebar and
        // the user menu visible on the login page.
        currentUserSubject.subscribe((user) => {
            // mirror the emitted user unconditionally - see AppComponent: the null pushed on logout
            // always arrives after 'loggedout' has been set, so honouring that flag here left a stale
            // user on the header after a session timeout.
            this.user = user;
            if (!this.user) {
                const lang = localStorage.getItem('language') || this.translateService.getCurrentLang() || 'EN';
                this.translateService.use(lang.toLowerCase());

                const msgs = <KendoMessageService>this.messageService;
                msgs.language = lang.toLowerCase();
            }
        });
    }

    toggleDarkMode() {
        this.dark = !this.dark;
        if (this.dark) {
            this.renderer.addClass(document.body, 'darkTheme');
        } else {
            this.renderer.removeClass(document.body, 'darkTheme');
        }
        localStorage.setItem('dark', JSON.stringify(this.dark));
    }

    ngOnInit() {
        this.dark = localStorage.getItem('dark') === 'true';
        if (this.dark) {
            this.renderer.addClass(document.body, 'darkTheme');
        } else {
            this.renderer.removeClass(document.body, 'darkTheme');
        }
        const storedLang = localStorage.getItem('language') || 'EN';
        this.translateService.use(storedLang.toLowerCase());

        const msgs = <KendoMessageService>this.messageService;
        msgs.language = storedLang.toLowerCase();

        if (environment.name === EnvironmentNames.STAGING) {
            this.buildInfo = 'TEST';
        }
        if (!this.user) this.getNotificationCount();
    }

    get currentLanguage() {
        return (this.translateService.getCurrentLang() || '').toUpperCase();
    }

    onToggleSidebar() {
        this.toggleSidebar.emit();
    }

    login() {
        this.router.navigate(['/login']);
    }

    logout() {
        this.dialogService
            .confirm(
                this.translateService.instant(marker('Sign Out')) as string,
                this.translateService.instant(marker('Are you sure you want to sign out?')) as string
            )
            .pipe(filter((x) => !(x instanceof DialogCloseResult)))
            .subscribe(() => {
                this.user = null;
                this.router.navigate(['/logout']);
                this.getNotificationCount();
            });
    }

    hasPermission(menuItem: MenuItem) {
        if (!this.user) {
            return false;
        }

        if (!menuItem.permissions || menuItem.permissions.length === 0) {
            return true;
        }

        return this.user && some(menuItem.permissions, (permission) => this.user.hasPermission(permission));
    }

    async changeLanguage() {
        const changeLanguageRef = this.kendoDialog.open({
            content: ChangeLanguageHeaderComponent,
            width: this.isMobile ? ModalWidth.xxl : ModalWidth.sm
        });

        changeLanguageRef.content.instance.currentLanguage = this.currentLanguage;
        changeLanguageRef.content.instance.title = this.translateService.instant(marker('Change Language'));
        changeLanguageRef.content.instance.availableLanguages = this.userLoggedOutMenuItems;

        let languageId;

        changeLanguageRef.result
            .pipe(
                filter((res) => !(res instanceof DialogCloseResult)),
                tap((x) => (languageId = x))
            )
            .subscribe((x) => {
                if (x) {
                    if (!this.user) {
                        this.translateService.use(languageId.toLowerCase());
                        this.toastyService.success(this.translateService.instant(marker('Language changed.')));
                        localStorage.setItem('language', languageId);

                        const msgs = <KendoMessageService>this.messageService;
                        msgs.language = languageId;
                    } else {
                        localStorage.setItem('language', languageId);

                        const msgs = <KendoMessageService>this.messageService;
                        msgs.language = languageId;

                        this.userService
                            .setCurrentLanguage(languageId, this.stateService.getSignInType())
                            .then((data) => {
                                if (!data) return;
                                this.userService.setAuthenticationTokens(data.accessToken, data.refreshToken);
                                this.translateService.use(languageId.toLowerCase());
                                this.toastyService.success(this.translateService.instant(marker('Language changed.')));
                                window.location.reload();
                            });
                    }
                }
            });
    }

    openNotificationsGridModal() {
        this.dialogService.openNotificationsGridModal(TabNotificationsComponent);
    }

    private getNotificationCount(): void {
        this.noticeBoardService.getNotificationCount().subscribe((notificationCount) => {
            this.notificationsCount = notificationCount;
        });
    }
}
